import { useRef } from "react";

/**
 * Captures how many leading items in `list` belong to its very first non-empty
 * render for the current `generationKey` (e.g. active filters/sort/status) —
 * used to give only that first-loaded batch an entrance animation, so cards
 * appended later via infinite scroll (or simply scrolled to) render already
 * settled instead of fading in.
 *
 * The count is captured once per generationKey, synchronously during render
 * (not via an effect), so there's no extra render pass where an item briefly
 * renders in the wrong (animated vs. static) branch.
 *
 * Usage: `index < useInitialBatchSize(list, generationKey)` decides, per item,
 * whether it was part of that first batch.
 */
export function useInitialBatchSize(list, generationKey) {
  const ref = useRef({ generationKey: null, count: 0 });

  if (ref.current.generationKey !== generationKey && list.length > 0) {
    ref.current = { generationKey, count: list.length };
  }

  return ref.current.generationKey === generationKey ? ref.current.count : 0;
}
